import { AfterViewInit, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import * as fromRoot from '../../../../../../../../state/app.state';
import { ProjectsService } from '../../../../../../core/projects.service';
import { AuthService } from '../../../../../../../../shared/services';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { allBidItems, basisOfPayOptions } from '../../../../../../core/projects.selectors';
import { isUnitPriceLocked, sharedPriceHint } from './unit-price-rules';

@Component({
    selector: 'app-change-in-work-request-line-item',
    templateUrl: './change-in-work-request-line-item.component.html',
    styleUrls: ['./change-in-work-request-line-item.component.scss'],
    standalone: false
})
export class ChangeInWorkRequestLineItemComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() lineKey: any;
  @Input() formGroup: any;
  @Input() pendingBidItems = [];
  alreadyExists = false;
  bidItemsList = [];
  filteredBidItemsList = [];
  filteredPendingItemsList = [];
  focusedIn = false;
  searchText: string;
  errorMessages: any = {};
  errorMessageMap = {
    searchText: { required: 'this field is required' },
    itemNo: {
      required: 'this field is required',
      duplicate: 'this item number already exists on the bid sheet',
    },
    item: { required: 'this field is required' },
    uom: { required: 'this field is required' },
    desc: { required: 'this field is required' },
    basisOfPay: { required: 'this field is required' },
    quantity: { required: 'this field is required' },
    changeInQuantity: { required: 'this field is required' },
    unitPrice: { required: 'this field is required' },
    totalAmount: { required: 'this field is required' },
    additionalWorkingDays: {},
    locationOfChange: {},
  };
  form = new FormGroup<any>({});
  totalAmount = 0;
  totalNewWorkingDays = 0;
  basisOfPayOptions = [];
  displayItemNo = false;

  isFormDirty = false;
  editable = true;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    public authService: AuthService,
    public dialog: MatDialog,
  ) {
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;

    // if click is NOT inside the search input or results list → close
    if (!target.closest('.search-section') && !target.closest('.bid-items-list')) {
      this.focusedIn = false;
      this.filteredBidItemsList = this.bidItemsList;
      this.filteredPendingItemsList = this.pendingBidItems;
    }
  }

  ngOnInit() {
    this.form = this.formGroup;

    // Hard block for new-item lines whose item number collides with a bid item
    // already on the bid sheet (or a pending COR item). itemNo is only enabled
    // in add-new mode, and disabled controls skip validators, so lines that
    // legitimately selected an existing/pending item are unaffected.
    this.form?.controls?.itemNo?.addValidators(this.duplicateItemNoValidator);

    this.form?.controls?.searchText?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(searchText => {
        this.searchText = searchText?.trim() || '';

        if (this.searchText) {
          const lowerSearch = this.searchText.toLowerCase();

          // filter list
          this.filteredBidItemsList = this.bidItemsList.filter(item => {
            const name = item.item?.toLowerCase() || '';
            const description = item.description?.toLowerCase() || '';
            return name.includes(lowerSearch) || description.includes(lowerSearch);
          });

          this.filteredPendingItemsList = this.pendingBidItems.filter(item => {
            const name = item.item_no?.toLowerCase() || '';
            const description = item.item_description?.toLowerCase() || '';
            return name.includes(lowerSearch) || description.includes(lowerSearch);
          });

          // check for exact match
          this.alreadyExists = this.bidItemsList.some(item =>
            item.item?.toLowerCase() === lowerSearch ||
            item.description?.toLowerCase() === lowerSearch,
          ) || this.pendingBidItems.some(item =>
            item.item_no?.toLowerCase() === lowerSearch ||
            item.item_description?.toLowerCase() === lowerSearch,
          );
        } else {
          this.filteredBidItemsList = this.bidItemsList;
          this.filteredPendingItemsList = this.pendingBidItems;
          this.alreadyExists = false;
        }
      });

    this.form?.controls.desc.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(descValue => {
        const itemNo = this.form.controls.itemNo.value || '';
        const combined = itemNo ? `${itemNo} ${descValue || ''}` : `${descValue || ''}`;

        this.form.patchValue({ item: combined });
      });

    this.form?.controls.itemNo.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(itemNoValue => {
        const desc = this.form.controls.desc.value || '';
        const combined = itemNoValue ? `${itemNoValue} ${desc}` : `${desc}`;

        this.form.patchValue({ item: combined });
        this.applyUnitPriceLock();
      });


    this.form?.controls.unitPrice.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.recomputeTotalAmount();
      });

    this.form?.controls?.changeInQuantity.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.recomputeTotalAmount();
      });

    this.form?.controls?.totalAmount.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data !== null && data !== undefined) {
          this.projectsService.totalAmount.next(data);
        }
      });

    this.form?.controls?.additionalWorkingDays.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data !== null && data !== undefined) {
          this.projectsService.totalAdditionalWorkingDays.next(data);
        }
      });

    this.projectsService.isAnyFormDirty
      .pipe(takeUntil(this.onDestroy))
      .subscribe((dirty) => {
        this.isFormDirty = dirty;
      });

    this.projectsService.checkForErrors
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.checkForErrors();
      });

    this.store.select(allBidItems)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((bidItems) => {
        this.bidItemsList = bidItems;
        this.filteredBidItemsList = bidItems;
        this.form?.controls?.itemNo?.updateValueAndValidity({ emitEvent: false });
        this.applyUnitPriceLock();
      });

    this.filteredPendingItemsList = this.pendingBidItems;

    this.store.select(basisOfPayOptions)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.basisOfPayOptions = data;
      });

    this.commonService.focusedIn
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.focusedIn = data;

          if (this.searchText) {
            this.filteredBidItemsList = this.bidItemsList.filter(item => {
              const name = item.item;
              const description = item.description;

              if (name?.toLowerCase().includes(this.searchText.toLowerCase())) {
                return true;
              }

              if (description.toLowerCase().includes(this.searchText.toLowerCase())) {
                return true;
              }
            });
          } else {
            this.filteredBidItemsList = this.bidItemsList;
          }
          this.filteredPendingItemsList = this.filterPendingItems(this.searchText);
        } else {
          this.filteredBidItemsList = [];
          this.filteredPendingItemsList = [];
          this.focusedIn = false;
        }
      });

    this.form?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value) => {
        const payload = {
          lineKey: this.lineKey,
          value,
        };
        this.projectsService.addValueToParentForm.next(payload);

        this.projectsService.isAnyFormDirty.next(true);
      });
  }

  ngAfterViewInit() {
    this.projectsService.isAnyFormDirty.next(false);
  }

  duplicateItemNoValidator = (control: any) => {
    // Only lines being created via "Add new bid item" in this session are
    // checked. Loaded lines are exempt: the parent's blanket form.enable() on
    // editable requests re-enables itemNo, so relying on control.enabled
    // alone would flag every saved line that references an existing bid item.
    if (!this.displayItemNo) {
      return null;
    }
    const value = String(control?.value || '').trim().toLowerCase();
    if (!value) {
      return null;
    }
    const existing = this.bidItemsList?.some(
      item => String(item?.item || '').trim().toLowerCase() === value,
    );
    const pending = this.pendingBidItems?.some(
      item => String(item?.item_no || '').trim().toLowerCase() === value,
    );
    return (existing || pending) ? { duplicate: true } : null;
  };

  /**
   * Amount change for the line. Existing-item lines account for repricing:
   * new_price * (existing_qty + delta) - existing_price * existing_qty, so a
   * price-only change (delta 0) still shows its dollar impact. New-item lines
   * are simply price * quantity.
   */
  private recomputeTotalAmount() {
    const newPrice = Number(this.form?.controls?.unitPrice?.value) || 0;
    const deltaQty = Number(this.form?.controls?.changeInQuantity?.value) || 0;
    const existingQty = Number(this.form?.controls?.quantity?.value) || 0;
    const oldPrice = this.existingUnitPrice;

    const total = oldPrice != null
      ? newPrice * (existingQty + deltaQty) - (Number(oldPrice) || 0) * existingQty
      : newPrice * deltaQty;

    this.form?.controls['totalAmount'].setValue(+total, { emitEvent: true });
  }

  /**
   * The unit price this line's change is measured against: the line's own
   * snapshot when present (stable after CO approval reprices the item),
   * falling back to the current bid sheet. Null for new/pending items.
   */
  get existingUnitPrice() {
    const snapshot = this.form?.controls?.originalUnitPrice?.value;
    if (snapshot != null) {
      return snapshot;
    }
    const itemNo = this.form?.controls?.itemNo?.value;
    if (!itemNo) {
      return null;
    }
    const matched = this.bidItemsList?.find(
      o => String(o?.item ?? '').trim() === String(itemNo).trim(),
    );
    return matched?.unit_price ?? null;
  }

  /**
   * Bid-sheet items carry their price from the bid sheet, so the unit price is
   * read-only on those lines; new/pending items keep an editable shared price.
   * Never enables the control while the whole form is disabled — the parent
   * blanket-disables non-editable (submitted/approved) requests.
   */
  applyUnitPriceLock() {
    const ctrl = this.form?.controls?.unitPrice;
    if (!ctrl) {
      return;
    }
    const locked = isUnitPriceLocked(
      this.form.controls.itemNo?.value,
      this.form.controls.bidItemId?.value,
      this.bidItemsList,
    );
    if (locked && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!locked && ctrl.disabled && this.form.enabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  get unitPriceHint(): string {
    // The bid-sheet hint explains why the field is locked, so it shows in both
    // states; the shared-price warning is only meaningful while the price can
    // still be edited, and is noise on a submitted/approved request
    if (isUnitPriceLocked(this.form?.controls?.itemNo?.value, this.form?.controls?.bidItemId?.value, this.bidItemsList)) {
      return this.existingUnitPrice != null ? `Price fixed by bid sheet: ${this.existingUnitPrice}` : 'Price fixed by bid sheet';
    }
    if (this.form?.disabled) {
      return '';
    }
    return sharedPriceHint(this.form?.controls?.itemNo?.value, this.pendingBidItems);
  }

  filterPendingItems(searchText: string) {
    if (!searchText) {
      return this.pendingBidItems;
    }
    const lowerSearch = searchText.toLowerCase();
    return this.pendingBidItems.filter(item => {
      const name = item.item_no?.toLowerCase() || '';
      const description = item.item_description?.toLowerCase() || '';
      return name.includes(lowerSearch) || description.includes(lowerSearch);
    });
  }

  selectBidItem(bidItem: any) {
    this.form?.patchValue({
      item: `${bidItem?.item} ${bidItem?.description}`,
      itemNo: bidItem?.item,
      desc: bidItem?.description,
      unitPrice: bidItem?.unit_price,
      quantity: bidItem?.quantity,
      originalUnitPrice: bidItem?.unit_price ?? null,
      uom: bidItem?.uom,
      bidItemId: bidItem?.id,
    });
    this.applyUnitPriceLock();
    this.focusedIn = false;
  }

  selectPendingItem(pendingItem: any) {
    this.form?.patchValue({
      item: `${pendingItem?.item_no} ${pendingItem?.item_description}`,
      itemNo: pendingItem?.item_no,
      desc: pendingItem?.item_description,
      unitPrice: pendingItem?.unit_price,
      quantity: pendingItem?.quantity,
      originalUnitPrice: null,
      uom: pendingItem?.unit,
      bidItemId: null,
    });
    this.applyUnitPriceLock();
    this.focusedIn = false;
  }

  addNewBidItem() {
    this.displayItemNo = true;
    this.form?.removeControl('searchText');
    this.form?.controls['itemNo'].enable();
    this.focusedIn = false;

    this.form.patchValue({
      itemNo: this.searchText,
      item: this.searchText,
      bidItemId: null,
      originalUnitPrice: null,
    });
    this.applyUnitPriceLock();
  }

  checkForErrors(currentField?: string) {
    if (currentField) {
      // Clear error first, then set if still invalid
      const errors = this.form.controls[currentField]?.errors;
      const firstErrorType = errors ? Object.keys(errors)[0] : '';
      if (firstErrorType && this.errorMessageMap[currentField]?.[firstErrorType]) {
        this.errorMessages[currentField] = this.errorMessageMap[currentField][firstErrorType];
      } else {
        delete this.errorMessages[currentField];
      }
    } else {
      this.errorMessages = {
        ...this.errorMessages,
        ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap)),
      };
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
